import vec2 from "@tlaukkan/tsm/dist/vec2";
import { on } from "events";
import { DragDropProtein, PreviewOnly, Transform } from "../../../shared/components";
import { Camera, CameraZoomLevel, PlayerConstants, RenderEffects, ShopConstants, Speeds } from "../../../shared/constants";
import { Debug } from "../../../shared/debug";
import { clearShopProteins, createEntityImmediate, destroyEntity, dropShopProtein, getEntityById } from "../../../shared/entityFactory";
import { GameState } from "../../../shared/game";
import { GameElement } from "../../../shared/gameElementTypes";
import { SyncMessageType } from "../../../shared/messageTypes";
import { Player, sell, spend } from "../../../shared/player";
import { Sound } from "../../../shared/sound";
import { wait } from "../../../shared/systems/animationSystem";
import { ChainSystem } from "../../../shared/systems/chainSystem";
import { ControlState, StateSystem } from "../../../shared/systems/stateSystem";
import { triggerEffect } from "../../../shared/triggeredEffects";
import { addComponent, calculateRoundShopTime, getSystem, metersToPixels } from "../../../shared/utils";
import { GameClient } from "../gameClient";
import { CameraSystem } from "../systems/cameraSystem";
import { ClientSyncSystem } from "../systems/clientSyncSystem";
import { PlacementSystem } from "../systems/placementSystem";
import { PointOfInterestSystem } from "../systems/pointOfInterestSystem";
import { RendererSystem } from "../systems/renderSystem";
import { SoundSystem } from "../systems/soundSystem";
import { getIngameTutorial } from "../userSettings";
import { ClientRecapState } from "./clientRecapState";
import { ClientSimulationState } from "./clientSimulationState";
import * as AchievementTracker from "../AchievementTracker";
import { beginRound, recordEarn, recordExperienceBought, recordImmuneCellBought, recordRefreshBought, recordSpend } from "../gameStats";

export class ClientShopState extends ControlState {
	isComplete = false;
	controller!: StateSystem;

	//TODO: These are in lobby/simulation
	placementSystem!: PlacementSystem;
	renderSystem!: RendererSystem;
	soundSystem!: SoundSystem;
	cameraSystem!: CameraSystem;
	chainSystem!: ChainSystem;

	private isLoadingState = false;
	private heartbeatTimer: number | undefined;

	private transitioningToSimulation = false;

	private botPlayer: Player | null = null;
	private holdMessages = false;
	private botTurnMaxTimeMs = 4000; //Maxiumum total time to focus on bot player
	private botMoveMaxTimeMs = 200; //Maximum time for a single bot move, if not many moves to show
	private botTurnTimeLeftMs = this.botTurnMaxTimeMs;
	private botMoveTimeLeftMs = 0;
	private botMoveLingerTimeMs = 500;
	private onBotTurnEnd = () => { };

	constructor(private gameClient: GameClient) {
		super();
	}

	getName(): GameState {
		return "shop"
	}

	async onEnter(controller: StateSystem) {
		this.controller = controller;

		//If the server couldn't load the opponent replay it falls back to single player, so stop treating this as ranked
		if (this.gameClient.isRankedGame && !this.gameClient.players.some(player => player.isBot)) {
			console.warn("Ranked opponent did not load, playing as single player");
			this.gameClient.isRankedGame = false;
			this.gameClient.requiredPlayers = 1;
		}

		this.placementSystem = getSystem(this.controller.world, PlacementSystem);
		this.placementSystem.setActivePlayer(this.gameClient.playerID);

		this.renderSystem = getSystem(this.controller.world, RendererSystem);

		this.cameraSystem = getSystem(this.controller.world, CameraSystem);
		//this.cameraSystem.lockToPlayerViews(true);

		this.chainSystem = getSystem(this.controller.world, ChainSystem);

		this.soundSystem = getSystem(this.controller.world, SoundSystem);

		//this.soundSystem.playM('shop');
		this.soundSystem.switchBackgroundMusic('shop');

		this.gameClient.playerHasEndedTurn = false;
		beginRound(this.gameClient.round, this.gameClient.getPlayer()?.currentFunds ?? 0);

		const timeToSimulation = (calculateRoundShopTime(this.gameClient) - ShopConstants.RecapStateTotalMS);
		if (timeToSimulation !== Infinity) {
			this.heartbeatTimer = window.setTimeout(() => {
				console.log('heartbeat sound')
				this.soundSystem.playUISound(Sound.Heartbeat);
			}, timeToSimulation - 5000); //10 seconds before simulation
		}

		//Show the bot player make its moves if there is one
		this.botPlayer = this.gameClient.players.find(player => player.id !== this.gameClient.playerID && player.isBot) || null;
		if (this.botPlayer) {
			this.holdMessages = true;
			this.gameClient.showPlayer(this.botPlayer.id);
			this.gameClient.disableMarket = true;
			await this.cameraSystem.setZoomLevel(CameraZoomLevel.Normal);
			this.holdMessages = false;
			const botMessagesInBuffer = this.gameClient.messageBuffer?.buffer.length || 0; //assume all messages are bot messages this early in shop
			const botTurnTime = Math.min(botMessagesInBuffer * this.botMoveMaxTimeMs, this.botTurnMaxTimeMs);
			this.botTurnTimeLeftMs = botTurnTime;
			this.botMoveTimeLeftMs = botTurnTime / botMessagesInBuffer;

			await new Promise<void>(resolve => {
				this.onBotTurnEnd = resolve;
			});
			await wait(this.botMoveLingerTimeMs); //looks better to linger a bit
		}

		this.gameClient.updateUI();
		this.gameClient.showPlayer(this.gameClient.playerID);

		this.gameClient.disableMarket = true;
		
		await this.cameraSystem.setZoomLevel(CameraZoomLevel.Normal);
		
		this.gameClient.disableMarket = false;
		this.gameClient.setHideMarket(true);
		
		this.cameraSystem.cameraSpeed = Speeds.CameraNormalSpeed;

		if (!Debug.SkipRecap) {
			this.gameClient.allowTutorial = true;
			this.gameClient.updateUI();
		}

		this.gameClient.trainingLevelUpdated = false;

		if (getIngameTutorial()) {
			setTimeout(() => {
				this.gameClient.tutorialStateMachine.onShopEnter();
				this.gameClient.updateUI();
			}, 600);

		}

		if (!this.gameClient.getPlayer()?.freezeShop) {
			this.gameClient.rerollNotication = true;
			this.gameClient.updateUI();
		}
	}

	async update(delta: number) {
		if (this.transitioningToSimulation) return; //If this gets any more complex, this should be its own state

		const start = this.gameClient.messageBuffer?.dequeueMessage(SyncMessageType.GotoSimulation)
		const reload = this.gameClient.messageBuffer?.dequeueMessage(SyncMessageType.GotoShop);
		if (start || reload) {
			//The step number must be updated here, immediately on getting the message
			//if we wait for the onComplete signal to go through from here it takes
			//a whole frame, and in the meantime frame one creation messages get
			//through and think we are further into simulation then we are.
			getSystem(this.controller.world, ClientSyncSystem).resetSteps();
			if (reload) {
				this.isComplete = true;
				this.isLoadingState = true;
				this.gameClient.round = reload.newRoundNumber;
				this.gameClient.latestVirusGrowth = reload.virusGrowth || null;
				this.gameClient.renderEngine.background.accelerateTrails(2.0);
			}
			else {
				this.transitionToSimulation();
			}
			return;
		}

		if (this.holdMessages === true) {
			return;
		}

		//Update apererture and market when we move over cells. Only change things when the state changes to avoid unnecessary updates and not interfere with other systems
		if (this.cameraSystem.isApertureFocused !== this.gameClient.focusedOnCells) {
			this.cameraSystem.setAperatureFocus(this.gameClient.focusedOnCells);
			this.gameClient.setHideMarket(!this.gameClient.focusedOnCells);
		}

		//Implement delayed processing of messages for bots so player can watch them play out
		if (this.botPlayer) {
			this.gameClient.showOpponentName = this.botPlayer.name;
			const topMessageType = this.gameClient.messageBuffer?.buffer[0]?.messageType;
			if (topMessageType === SyncMessageType.ShopConfirmBuy
				|| topMessageType === SyncMessageType.ShopConfirmSell
				|| topMessageType === SyncMessageType.ShopForceMove
				|| topMessageType === SyncMessageType.ShopDragTrigger
			) {
				const messagesRemaining = this.gameClient.messageBuffer?.buffer.length || 0;
				this.botTurnTimeLeftMs -= delta;
				if (this.botTurnTimeLeftMs > 0 && messagesRemaining > 1) {
					this.botMoveTimeLeftMs -= delta;
					if (this.botMoveTimeLeftMs > 0) {
						return; //If we are still waiting, just return
					}
					else {
						this.botMoveTimeLeftMs = messagesRemaining === 0 ? 0 : this.botTurnTimeLeftMs / messagesRemaining;
					}
				}
			}
			else {
				const confirmBotTurnEnd = this.gameClient.messageBuffer?.dequeueMessage(SyncMessageType.BotMoveEnd);
				if (confirmBotTurnEnd) {
					this.onBotTurnEnd();
					this.botPlayer = null; //not used elsewhere so just using this to signal that we don't need to check for bot moves anymore
					this.gameClient.showOpponentName = "";
					return;
				}
			}
		}

		const confirmBuy = this.gameClient.messageBuffer?.dequeueMessage(SyncMessageType.ShopConfirmBuy)
		if (confirmBuy) {
			const player = this.gameClient.players.find(player => player.id === confirmBuy.playerId);
			if (!player) return; //TODO: This state should be just impossible
			const isClientPlayer = player === this.gameClient.getPlayer();

			const position = new vec2(confirmBuy.item.position);

			//Add to local comp
			const compElement = { type: confirmBuy.item.type, position: position.xy, rotation: 0, id: confirmBuy.item.id, combined: 0 };
			this.gameClient.addCompositionElement(player.immuneCells, compElement);

			if (isClientPlayer) {
				recordImmuneCellBought(confirmBuy.item.type);
				if (confirmBuy.isReward) {
					player.rewardOffer.splice(confirmBuy.offerIndex, 1);
				}
				else {
					spend(player, PlayerConstants.ImmuneCellCost);
					recordSpend(PlayerConstants.ImmuneCellCost);
					player.offer.splice(confirmBuy.offerIndex, 1);
				}
			}

			//Add to world
			const localEntity = createEntityImmediate(this.controller.world, {
				type: confirmBuy.item.type,
				position: position.xy,
				rotation: compElement.rotation,
				playerId: player.id,
				unsynced: true,
				sourceCompElement: compElement
			});


			this.soundSystem.playSound(Sound.WaterPlop, position);

			this.gameClient.updateUI();

			if (localEntity) {
				this.controller.sharedOnBuy(localEntity, compElement, player);
				this.placementSystem.addMovedUntilNextFrame(localEntity, compElement.id);
			}

			if (getIngameTutorial()) {
				this.gameClient.tutorialStateMachine.onBuyCell(compElement);
				this.gameClient.updateUI();
			}

			//Don't process other messages on the same frame as buy. This can be an issue on bot turns, but blocking it generally
			//because its a good idea to let things settle after a buy
			return;
		}


		const confirmSell = this.gameClient.messageBuffer?.dequeueMessage(SyncMessageType.ShopConfirmSell);
		if (confirmSell) {
			const player = this.gameClient.players.find(player => player.id === confirmSell.playerId);
			if (player) {
				const compElement = this.gameClient.getCompElementById(confirmSell.id);
				if (!compElement) throw new Error('ClientShopState: Tried to sell a non-existant id');
				const entity = getEntityById(compElement.id);
				if (entity) {
					destroyEntity(entity, true);
				}
				const fundsBeforeSale = player.currentFunds;
				sell(player, compElement);
				if (confirmSell.playerId === this.gameClient.playerID) {
					recordEarn(player.currentFunds - fundsBeforeSale);
				}
				this.gameClient.removeCompositionElement(player.immuneCells, compElement.id);

				AchievementTracker.onSellImmuneCell(this.gameClient);
			}

			this.gameClient.updateUI();
		}

		do {
			let forceMove = this.gameClient.messageBuffer?.dequeueMessage(SyncMessageType.ShopForceMove);
			if (forceMove) {
				if (forceMove.playerId !== this.gameClient.playerID || forceMove.isUndo) {
					const compElement = this.gameClient.getCompElementById(forceMove.id);
					if (!compElement) {
						throw new Error(`ClientShopState: Tried to ${forceMove.isUndo ? 'undo' : 'force'} a move on a non-existant entity`);
					}
					compElement.position = forceMove.position;
					compElement.rotation = forceMove.rotation;
					const placement = getSystem(this.gameClient.world, PlacementSystem);
					placement.handleShopInitiatedMovement(forceMove)
					if (forceMove.isUndo) {
						console.warn("Server rejected a move from client. This usually indicates a client issue sending an invalid position");
					}
				}
			}
		}
		//Repeat if a group move, keep repeating it
		while (this.gameClient.messageBuffer!.buffer.length > 0 && this.gameClient.messageBuffer!.buffer[0].messageType === SyncMessageType.ShopForceMove && this.gameClient.messageBuffer!.buffer[0].isGroupMove);

		const confirmExp = this.gameClient.messageBuffer?.dequeueMessage(SyncMessageType.ShopConfirmExp);
		if (confirmExp) {
			//Only ever sent to the local player, never broadcast
			recordExperienceBought();
			recordSpend(PlayerConstants.ExperienceCost);
			if (confirmExp.didLevel && getIngameTutorial()) {
				this.gameClient.tutorialStateMachine.onLevelUp();
			}
			this.gameClient.updateUI();
		}

		const confirmRefresh = this.gameClient.messageBuffer?.dequeueMessage(SyncMessageType.ShopConfirmRefresh);
		if (confirmRefresh) {
			//Only ever sent to the local player, never broadcast
			recordRefreshBought();
			recordSpend(PlayerConstants.RefreshShopCost);
			this.gameClient.rerollNotication = true;
			this.gameClient.updateUI();
		}

		const confirmReward = this.gameClient.messageBuffer?.dequeueMessage(SyncMessageType.PlayerReward);
		if (confirmReward) {
			const player = this.gameClient.getPlayer();
			if (player) {
				player.rewardOptions = confirmReward.reward;
				this.gameClient.updateUI();
			}
		}

		const confirmDragTrigger = this.gameClient.messageBuffer?.dequeueMessage(SyncMessageType.ShopDragTrigger);
		if (confirmDragTrigger) {
			const entity = getEntityById(confirmDragTrigger.dropperId);
			const player = this.gameClient.players.find(player => player.id === confirmDragTrigger.playerId);
			if (!entity) throw new Error('ClientShopState: Drag trigger entity not found');
			if (!player) throw new Error('ClientShopState: Drag trigger player not found');

			const dropper = entity.getComponent(DragDropProtein);
			if (!dropper) throw new Error('ClientShopState: Drag trigger entity has no DragDropProtein component');

			clearShopProteins(player, confirmDragTrigger.dropperId, this.gameClient.getCurrentRound());
			dropper.lastPosition = new vec2(confirmDragTrigger.atPosition);
			if (dropper.triggers > 0) {
				--dropper.triggers;
			}
			if (confirmDragTrigger.droppedId !== -1) {
				dropShopProtein(this.gameClient, player, confirmDragTrigger.dropperId, confirmDragTrigger.droppedId, dropper.proteinType, dropper.lastPosition.xy);
				if (confirmDragTrigger.playerId === this.gameClient.playerID) {
					this.soundSystem.playSound(Sound.ProducedResource, dropper.lastPosition);
				}
			}
		}
	}

	previewBuyCell(type: GameElement, index: number, isReward: boolean) {
		const mousepos = this.placementSystem.getWorldMouseCoords().xy;
		//Add to world
		const entity = createEntityImmediate(this.controller.world, {
			type,
			position: mousepos,
			rotation: 0,
			playerId: this.gameClient.playerID,
			unsynced: true,
			previewOnly: true
		});

		if (!entity) throw new Error("ClientShopState: Preview entity creation faile");

		this.placementSystem.forceDragEntity(entity);

		entity.getComponent(PreviewOnly).onEndPreview = (placementValid: boolean) => {
			this.gameClient.sendBuyAntibody(index, isReward, type, entity.getComponent(Transform).position);
			this.gameClient.setInspection(null);
		};

		if (getIngameTutorial()) {
			this.gameClient.tutorialStateMachine.onPreviewBuyCell(type);
			this.gameClient.updateUI();
		}
	}

	async transitionToSimulation() {
		console.log('transitioning to simulation');
		this.transitioningToSimulation = true;
		this.gameClient.allowTutorial = false;
		this.cameraSystem.lockToPlayerViews(false);

		//Disable sync system so it doesn't try to process any messages while we are transitioning
		const syncSystem = getSystem(this.controller.world, ClientSyncSystem)
		syncSystem.enabled = false;

		this.cameraSystem.setAperatureFocus(false, undefined, 0.6);
		this.gameClient.updateUI();
		window.clearTimeout(this.heartbeatTimer);


		//TODO: Find where the point of interest center will be, and transition gracefully to it
		const pointOfInterestSystem = getSystem(this.controller.world, PointOfInterestSystem);
		pointOfInterestSystem.updatePointsOfInterest(this.gameClient.getPlayer()!);
		const rect = pointOfInterestSystem.calculateRectangleOfInterest()!;
		// await this.cameraSystem.focusOnPosition(new vec2([rect.x + rect.width / 2, rect.y + rect.height / 2]));

		// await wait(ShopConstants.ShopExitTransitionTimeSeconds * 1000);
		this.transitioningToSimulation = false;
		syncSystem.enabled = true; //Re-enable sync system
		console.log('complete transitioning to simulation');
		this.isComplete = true;
	}

	onExit(controller: StateSystem): void {
		this.placementSystem.setActivePlayer(null);
		if (this.isLoadingState) {
			controller.pushState(new ClientRecapState(this.gameClient));
			this.isLoadingState = false;
		}
		else {
			controller.pushState(new ClientSimulationState(this.gameClient));
		}

	}
}
